import React from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { useAlbumDetail, useAlbumDetailBySlug } from './hooks/useAlbumDetail';
import { useRelatedAlbums } from './hooks/useRelatedAlbums';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import { Grid, Typography, Box, Stack, Link, useMediaQuery } from '@mui/material';
import PageTitle from '../../components/common/PageTitle';
import SidebarLayout from '../../components/layout/SidebarLayout';
import theme from '../../theme';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import explicitIcon from '../../assets/explicit_icon.svg';

const RelatedAlbumsViewAll = () => {
  const params = useParams();
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const location = useLocation();

  const slugResult = useAlbumDetailBySlug(albumSlug);
  const { data: directAlbumDetail, isLoading: directLoading } = useAlbumDetail(
    paramSource,
    paramId,
  );

  const albumDetail = albumSlug ? slugResult.data : directAlbumDetail;
  const albumLoading = albumSlug ? slugResult.isLoading : directLoading;
  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;

  const albumUrl = albumSlug ? `/reviews/${albumSlug}` : `/reviews/${albumSource}/${sourceId}`;

  const { data: albums = [], isLoading: albumsLoading } = useRelatedAlbums(
    albumDetail?.sourceId,
    albumDetail?.albumSource,
  );

  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_POST,
      contentType: CONTENT_TYPE.ALBUM,
      contentId: sourceId,
      contentName: albumDetail?.title,
      reviewsSection: 'recommended_album',
    },
    { ready: !!sourceId },
  );

  const artistName = location.state?.artistName || albumDetail?.artist?.name || '';
  const albumTitle = location.state?.albumTitle || albumDetail?.title || '';
  const loading = albumLoading || albumsLoading;
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <PageLayout>
      <ProgressBar isLoading={loading}>
        <SidebarLayout pageType={PAGE_TYPE.REVIEWS_POST}>
          <Stack spacing={{ xs: 2, md: 2.5, lg: 3.75 }}>
            <Stack spacing={{ xs: 1.5, md: 1.5, lg: 2 }}>
              {(artistName || albumTitle) && (
                <Link component={RouterLink} to={albumUrl} sx={{ textDecoration: 'none' }}>
                  <Typography
                    fontSize={{ xs: 14, md: 14, lg: 16 }}
                    color={theme.palette.black[6]}
                    fontWeight={'medium'}
                    sx={{ display: 'inline' }}
                  >
                    {[artistName, albumTitle].filter(Boolean).join(' - ')}
                  </Typography>
                  {albumDetail?.contentRating === 'EXPLICIT' && (
                    <Box
                      component="img"
                      src={explicitIcon}
                      alt="Explicit"
                      sx={{
                        width: { xs: '12px', lg: '14px' },
                        height: { xs: '12px', lg: '14px' },
                        ml: 0.75,
                        verticalAlign: '-0.08em',
                      }}
                    />
                  )}
                </Link>
              )}
              <PageTitle>좋아할 만한 다른 앨범</PageTitle>
            </Stack>
            {albums.length > 0 ? (
              <Box sx={{ overflow: 'hidden' }}>
                <Grid
                  container
                  rowSpacing={{ xs: 3, md: 5, lg: 5 }}
                  columnSpacing={{ xs: 2, md: 2, lg: 2.5 }}
                  sx={{ mt: { xs: -3, md: -5, lg: -5 } }}
                >
                  {albums.map((album, index) => (
                    <Grid item xs={6} md={3} key={`${album.sourceId}-${index}`}>
                      <AlbumVerticalCard
                        albumId={album.albumId}
                        sourceId={album.sourceId}
                        albumSource={album.albumSource}
                        slug={album.slug}
                        imageUrl={album.albumImageUrl}
                        albumName={album.title}
                        artistId={album.artist?.sourceId}
                        artistDbId={album.artist?.id}
                        artistSource={album.artist?.artistSource}
                        artistName={album.artist?.name}
                        artistSlug={album.artist?.slug}
                        contentRating={album.contentRating}
                        albumAvgRating={album.averageRating ?? 0}
                        commentCount={album.commentCount ?? 0}
                        bookmarkCount={album.bookmarkCount ?? 0}
                        isStarred={album.isStarred}
                        releaseDate={album.releaseDate}
                        ratingSolo={!isLg}
                        dateSolo={isLg}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
                <Typography variant="body1" color="text.secondary">
                  {loading ? '로딩 중...' : '관련 앨범이 없습니다.'}
                </Typography>
              </Box>
            )}
          </Stack>
        </SidebarLayout>
      </ProgressBar>
    </PageLayout>
  );
};

export default RelatedAlbumsViewAll;
